import React, { useState } from 'react';

// ── Métadonnées activités ──
export const ACTIVITY_META = {
  subscriptions: { label: 'Abonnements', color: '#10b981' },
  taxi: { label: 'Taxi', color: '#FF7800' },
  delivery: { label: 'Livraison', color: '#FFB900' },
  rental: { label: 'Location', color: '#FF7800' },
  lodging: { label: 'Hébergement', color: '#06b6d4' },
};
export const ACTIVITY_ORDER = ['subscriptions', 'taxi', 'delivery', 'rental', 'lodging'];

export const EXPENSE_CAT_LABELS = {
  salaries: 'Salaires & charges', servers: 'Infrastructure / serveurs', marketing: 'Marketing',
  rent: 'Loyer', fees: 'Frais bancaires', taxes: 'Impôts & taxes', equipment: 'Matériel', other: 'Divers',
};

export const SEVERITY_META = {
  high: { label: 'Critique', bg: '#fee2e2', color: '#dc2626' },
  medium: { label: 'À surveiller', bg: '#fef3c7', color: '#FF9B00' },
  low: { label: 'Faible', bg: '#FEE9E2', color: '#C9350F' },
  info: { label: 'Info', bg: '#F4F5F7', color: '#414852' },
  ok: { label: 'OK', bg: '#ECFDF5', color: '#10B981' },
};

const PRESETS = [
  { key: '30days', label: '📅 30 j', color: '#FF7800' },
  { key: '1year', label: '📆 1 an', color: '#FF7800' },
  { key: 'alltime', label: '🕐 All Time', color: '#10b981' },
];

// ── Formatage monétaire (conversion d'affichage CDF→USD) ──
export function fmtMoney(cdf, currency, rate) {
  const n = cdf || 0;
  if (currency === 'USD') {
    return `$${(n / (rate || 2800)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${Math.round(n).toLocaleString('fr-FR')} FC`;
}

export const fmtInt = (n) => (n || 0).toLocaleString('fr-FR');
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// ── Cartes & blocs réutilisables ──
export function Card({ children, style }) {
  return <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', ...style }}>{children}</div>;
}

export function KpiCard({ label, value, color = '#1E2229', border = '#FF7800', sub }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${border}` }}>
      <p style={{ fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.4rem', textTransform: 'uppercase', fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: '1.6rem', fontWeight: 700, color, margin: '0.25rem 0' }}>{value}</p>
      {sub && <p style={{ fontSize: '0.7rem', color: '#9AA0A8', margin: 0 }}>{sub}</p>}
    </div>
  );
}

export function BarRow({ label, amount, pct, color, valueText }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontWeight: 600, color: '#1E2229', fontSize: '0.9rem' }}>{label}</span>
        <span style={{ color: '#5C636E', fontSize: '0.85rem' }}>{valueText} <span style={{ color: '#9AA0A8' }}>({pct.toFixed(1)}%)</span></span>
      </div>
      <div style={{ background: '#E9EBEF', borderRadius: '8px', height: '20px', overflow: 'hidden' }}>
        <div style={{ background: color, height: '100%', width: `${Math.min(100, pct)}%`, transition: 'width 0.3s ease' }}></div>
      </div>
    </div>
  );
}

export function Spinner({ label = 'Chargement…' }) {
  return <div className="loading-spinner"><div className="spinner"></div><p>{label}</p></div>;
}

// ── Hook de contrôles de période (presets + plage perso + groupBy) ──
export function usePeriodControls(defaultPreset = '1year', defaultGroupBy = 'month') {
  const [params, setParams] = useState({ mode: 'preset', preset: defaultPreset, start: '', end: '', groupBy: defaultGroupBy });
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');

  const qs = () => {
    const p = new URLSearchParams({ groupBy: params.groupBy });
    if (params.mode === 'custom') { p.set('start', params.start); p.set('end', params.end); }
    else p.set('preset', params.preset);
    return p.toString();
  };

  const controls = ({ showGroupBy = true } = {}) => (
    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
      {PRESETS.map(({ key, label, color }) => {
        const active = params.mode === 'preset' && params.preset === key;
        return (
          <button key={key} onClick={() => setParams(x => ({ ...x, mode: 'preset', preset: key }))}
            style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.82rem',
              background: active ? color : '#f0f4f8', color: active ? 'white' : '#414852', fontWeight: active ? 700 : 600 }}>
            {label}
          </button>
        );
      })}
      <span style={{ width: 1, height: 22, background: '#E9EBEF' }} />
      <input type="date" value={draftStart} onChange={e => setDraftStart(e.target.value)} style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.82rem' }} />
      <input type="date" value={draftEnd} onChange={e => setDraftEnd(e.target.value)} style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.82rem' }} />
      {showGroupBy && (
        <select value={params.groupBy} onChange={e => setParams(x => ({ ...x, groupBy: e.target.value }))}
          style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.82rem', background: 'white' }}>
          <option value="day">Par jour</option>
          <option value="month">Par mois</option>
        </select>
      )}
      <button className="btn-primary" style={{ padding: '0.4rem 1rem' }}
        onClick={() => { if (draftStart && draftEnd) setParams(x => ({ ...x, mode: 'custom', start: draftStart, end: draftEnd })); }}>
        Appliquer
      </button>
      <button className="btn-secondary" style={{ padding: '0.4rem 1rem' }}
        onClick={() => { setDraftStart(''); setDraftEnd(''); setParams({ mode: 'preset', preset: defaultPreset, start: '', end: '', groupBy: defaultGroupBy }); }}>
        Réinitialiser
      </button>
    </div>
  );

  return { params, qs, groupBy: params.groupBy, controls };
}
